import React, { useCallback, useMemo, useRef } from 'react';
import {
  DomainTuple,
  VictoryAxis,
  VictoryChart,
  VictoryThemeDefinition,
  VictoryZoomContainer,
} from 'victory';
import {
  Datum,
  TOOLTIP_HEIGHT,
  TOOLTIP_WIDTH,
  TOOLTIP_X_OFFSET,
} from './ResultChartComponent';
import {
  ResultHistory,
  isResultHistoryWithRange,
} from '../../../../../../../services/MedicalResultsService';
import { TimeScale } from '../TimeScalePicker';
import {
  getObservationNumericValue,
  getObservationUnit,
  getObservationValueDisplay,
} from '../../../util/fhir';
import {
  ResultViewMode,
  getEffectiveStatusForLens,
} from '../../../util/annotated-results';
import moment from 'moment';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import { scaleLinear } from 'd3-scale';
import memoize from 'lodash/memoize';
import { dateMomentRange, getColorByStatus } from '../../../util/graphUtils';
import { CustomScatterChart } from './CustomScatterChart';
import { Text } from '@lifeomic/chroma-react/components/Text';

import { Theme, makeStyles } from '@lifeomic/chroma-react/styles';
import { theme } from '../../../../../../../theme/phc';
import { Legend } from './Legend';

type MedicalResultChartProps = {
  onResultClick: (datum: Datum) => void;
  selectedResourceId: string | undefined;
  medicalResults: ResultHistory[] | undefined;
  selectedTimeScale: TimeScale;
  resultViewMode?: ResultViewMode;
};

const CHART_AREA_HEIGHT = 250;
const CHART_AREA_WIDTH = 250;

type TimeScaleDomainSettings = {
  period: 'month' | 'year';
  count: number;
  range: 'day' | 'month' | 'year';
  rangeStep: number;
  domainSize: number;
};

const useStyles = makeStyles((theme: Theme) => ({
  selectedResourceContainer: {
    top: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const chartTheme: VictoryThemeDefinition = {
  axis: {
    style: {
      axis: {
        stroke: theme.palette.grey[500],
      },
      grid: {
        stroke: theme.palette.grey[500],
      },
      tickLabels: {
        stroke: 'transparent',
        strokeWidth: 0,
        fill: theme.palette.common.black,
        padding: 8,
        ...theme.typography.subtitle1,
        fontSize: 12, // victory-chart doesn't like rem based font sizes
      },
      axisLabel: {
        fill: theme.palette.common.black,
      },
    },
  },
};

const timeScales: Record<TimeScale, TimeScaleDomainSettings> = {
  m: {
    period: 'month',
    count: 24,
    range: 'day',
    rangeStep: 7,
    domainSize: 4,
  },
  '6m': {
    period: 'month',
    count: 24,
    range: 'month',
    rangeStep: 1,
    domainSize: 6,
  },
  y: {
    period: 'month',
    count: 24,
    range: 'month',
    rangeStep: 1,
    domainSize: 12,
  },
  '5y': {
    period: 'year',
    count: 5,
    range: 'year',
    rangeStep: 1,
    domainSize: 5,
  },
} as const;

const isDefined = <T extends unknown>(value: T): value is NonNullable<T> =>
  value !== undefined && value !== null;

export const ResultChart = React.memo(
  ({
    medicalResults = [],
    onResultClick,
    selectedResourceId,
    selectedTimeScale,
    resultViewMode = 'standard',
  }: MedicalResultChartProps) => {
    const classes = useStyles({});
    const zoomEpsilon = useRef(0);

    const selectedResource = medicalResults
      .map(_ => _.resource)
      .find(_ => _?.id === selectedResourceId);

    const toSelectedResourceLabel = useCallback(() => {
      if (!selectedResource || !selectedResource.effectiveDateTime) return '';
      const value = getObservationValueDisplay(selectedResource);
      const unit = getObservationUnit(selectedResource);
      return `${moment(selectedResource.effectiveDateTime).format(
        'M/D/YYYY',
      )} | ${value} ${unit}`;
    }, [selectedResource]);

    /**
     * The filter causes a new array to be returned every render so we memoize here to
     * remove unnecessary rerenders of components and other hooks that reference data.
     */
    const data: Datum[] = useMemo(
      () =>
        medicalResults
          .map(_ => {
            const effectiveDateTime =
              _.resource?.effectiveDateTime ||
              _.resource?.effectivePeriod?.end ||
              _.resource?.effectivePeriod?.start;
            if (!effectiveDateTime) return null;
            const yValue = getObservationNumericValue(
              _.resource as fhir.Observation,
            );
            const rangeLowFallback = (() => {
              const referenceRangeLow =
                _.resource?.referenceRange?.[0].low?.value ?? 0;
              if (isResultHistoryWithRange(_)) {
                const ranges = _.ranges || {};
                return (
                  minBy(Object.values(ranges), 'lower')?.lower ??
                  referenceRangeLow
                );
              }
              return referenceRangeLow;
            })();
            const effectiveStatus = getEffectiveStatusForLens(
              _,
              resultViewMode,
            );
            return {
              x: new Date(effectiveDateTime),
              y: yValue ?? rangeLowFallback, // Determines placement on the chart. Attempt to fallback to low end of the reference range
              id: _.resource?.id,
              value: getObservationValueDisplay(_.resource as fhir.Observation), // displayed to user
              status: _.type === 'withRange' ? effectiveStatus : undefined,
              color: getColorByStatus(effectiveStatus),
            };
          })
          .filter((d): d is NonNullable<typeof d> => isDefined(d)),
      [medicalResults, resultViewMode],
    );

    /**
     * useMemo prevents other hooks and components from rerendering unnecessarily
     */
    const firstResult = useMemo(() => medicalResults[0], [medicalResults]);

    /**
     * Ranges will not change when the time scale changes so we use useMemo to prevent the domain from being recalculated on every render.
     */
    const [minFromRanges, maxFromRanges] = useMemo(() => {
      if (isResultHistoryWithRange(firstResult)) {
        const ranges = Object.values(firstResult.ranges || {});
        const lower = minBy(ranges, 'lower')?.lower ?? Infinity;
        const upper = maxBy(ranges, 'upper')?.upper ?? -Infinity;
        return [lower, upper];
      }
      return [Infinity, -Infinity];
    }, [firstResult]);

    /**
     * The domainDates (ticks) only change when the scale changes. Use useMemo here
     * to prevent recalculating the ticks on point selection changes.
     */
    const domainDates: Date[] = useMemo(() => {
      const timeScale = timeScales[selectedTimeScale];
      const start = moment()
        .startOf(timeScale.period)
        .subtract(timeScale.count, timeScale.period);
      const end = moment().endOf(timeScale.period).add(1, timeScale.range);
      return dateMomentRange(
        start,
        end,
        timeScale.range,
        timeScale.rangeStep,
      ).map(_ => _.toDate());
    }, [selectedTimeScale]);

    /**
     * The domain only changes when data changes. Use useMemo here
     * to prevent recalculating the domain on point selection changes.
     */
    const domain = useMemo(() => {
      const referenceRange = !firstResult
        ? [0, 0]
        : [
            firstResult.resource?.referenceRange?.[0]?.low?.value || 0,
            firstResult.resource?.referenceRange?.[0]?.high?.value,
          ];

      const xDomain: DomainTuple = [
        domainDates[0],
        domainDates[domainDates.length - 1],
      ];
      const yDomain = [
        minBy([...data, { y: referenceRange[0] }, { y: minFromRanges }], 'y')
          ?.y,
        maxBy([...data, { y: referenceRange[1] }, { y: maxFromRanges }], 'y')
          ?.y,
      ] as [number, number];

      const yScale = scaleLinear()
        .domain(yDomain)
        .range([0, CHART_AREA_HEIGHT]);
      const yMaxDomainPadding = yScale.invert(TOOLTIP_HEIGHT) - yDomain[0];
      const timeScale = timeScales[selectedTimeScale];
      // Zooming causes multiple "pages" of data.
      // We need the domain to encompass all pages for accurate padding calculations.
      const pages = timeScale.count / timeScale.domainSize;
      const xScale = scaleLinear()
        .domain(xDomain)
        .range([0, pages * CHART_AREA_WIDTH]);
      // Add padding to the right of the chart so tooltips don't get cut off
      const xMaxDomainPadding =
        xScale.invert(TOOLTIP_WIDTH / 2 + 2 * TOOLTIP_X_OFFSET) -
        Number(xDomain[0]);

      return {
        x: [
          Number(xDomain[0]),
          Number(xDomain[1]) + xMaxDomainPadding,
        ] as DomainTuple,
        y: [yDomain[0], yDomain[1] + yMaxDomainPadding] as [number, number],
      };
    }, [
      data,
      domainDates,
      firstResult,
      minFromRanges,
      maxFromRanges,
      selectedTimeScale,
    ]);

    /**
     * The zoomDomain only changes when the scale changes. Use useMemo here
     * to prevent zoom rerendering on point selection changes.
     */
    const zoomDomain: { x: DomainTuple; y: DomainTuple } = useMemo(() => {
      // Update the epsilon value to force the zoom container
      // to scroll to the beginning. Victory charts caches
      // based on the zoom domain so we change it just enough to reset the zoom.
      zoomEpsilon.current += 0.001;
      // we don't need to apply the epsilon for the 5y scale because we don't allow zooming for it
      const epsilon = selectedTimeScale === '5y' ? 0 : zoomEpsilon.current;
      const xDomainStart =
        domainDates[
          domainDates.length - (timeScales[selectedTimeScale].domainSize + 1)
        ];

      const scale = {
        x: [Number(xDomainStart) - epsilon, Number(domain.x[1])] as [
          number,
          number,
        ],
        y: domain.y,
      };

      // Practically shouldn't matter but this is a safety net to prevent an infinite loop
      let maxAdjustments = 5;
      do {
        const xScale = scaleLinear()
          .domain(scale.x)
          .range([0, CHART_AREA_WIDTH]);
        const tooltipHalfWidth = xScale.invert(TOOLTIP_WIDTH / 2) - scale.x[0];
        const tooltipOffset = xScale.invert(3 * TOOLTIP_X_OFFSET) - scale.x[0];
        const partiallyVisibleOnLeftPoint = data.find(_ => {
          const dRight = Number(_.x) + tooltipHalfWidth;
          const dLeft = Number(_.x) - tooltipHalfWidth;
          return dRight >= scale.x[0] && dLeft <= scale.x[0];
        });

        if (!partiallyVisibleOnLeftPoint) {
          break;
        }

        const d =
          Number(partiallyVisibleOnLeftPoint.x) -
          tooltipHalfWidth -
          tooltipOffset;

        scale.x[0] = d;
      } while (--maxAdjustments > 0);

      return scale;
    }, [data, selectedTimeScale, domainDates, domain.y, domain.x]);

    /**
     * The tickFormat only changes when the scale changes. Use useMemo here
     * to prevent modifying the function when point selection occurs. Memoize
     * the returned function to prevent computing the label on every chart
     * interaction (click, scroll)
     */
    const tickFormat = useMemo(
      () =>
        memoize((v: Date) => {
          switch (selectedTimeScale) {
            case 'm':
              return moment(v).format('M/D');
            case '6m':
              return moment(v).format('MMM');
            case 'y':
              return moment(v).format('MMM')[0];
            case '5y':
              return moment(v).format('YYYY');
          }
        }),
      [selectedTimeScale],
    );

    const zoomDisabled = selectedTimeScale === '5y';

    return (
      <div>
        <div className={classes.selectedResourceContainer}>
          {selectedResource && (
            <Text>{getObservationUnit(selectedResource)}</Text>
          )}
          <Text>{toSelectedResourceLabel()}</Text>
        </div>
        <VictoryChart
          theme={chartTheme}
          style={{
            background: {
              fill: 'transparent',
              stroke: theme.palette.grey[500],
              strokeWidth: 1,
            },
          }}
          domain={domain}
          containerComponent={
            <VictoryZoomContainer
              zoomDomain={zoomDomain}
              disable={zoomDisabled}
              zoomDimension="x"
              allowZoom={false}
            />
          }
          height={212}
          width={480}
          padding={{ top: 8, right: 16, bottom: 24, left: 70 }}
        >
          <VictoryAxis tickValues={domainDates} tickFormat={tickFormat} />
          <VictoryAxis
            dependentAxis
            style={{
              axis: {
                stroke: 'transparent',
              },
            }}
          />
          <CustomScatterChart
            data={data}
            selectedResourceId={selectedResourceId}
            onClick={onResultClick}
            initialZoomDomain={zoomDomain}
          />
        </VictoryChart>

        <Legend />
      </div>
    );
  },
);
